/**
 * Finance Service Module
 * Centralized service for finance operations and calculations
 */

const FinanceService = {
  /**
   * Format currency to UGX (without dollar symbol)
   */
  formatCurrency: (value, options = {}) => {
    if (value === null || value === undefined || isNaN(value)) return "UGX 0";

    const defaultOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };

    const formatted = new Intl.NumberFormat("en-US", {
      ...defaultOptions,
      ...options,
    }).format(value);
    return `UGX ${formatted}`;
  },

  /**
   * Calculate balance
   */
  calculateBalance: (amountDue, amountPaid) => {
    return parseFloat(amountDue) - parseFloat(amountPaid);
  },

  /**
   * Determine payment status
   */
  getPaymentStatus: (amountDue, amountPaid) => {
    const balance = FinanceService.calculateBalance(amountDue, amountPaid);

    if (balance === 0) return "Fully Paid";
    if (amountPaid > 0) return "Partially Paid";
    return "Pending";
  },

  /**
   * Determine if fee is overdue
   */
  isOverdue: (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const today = new Date();
    return today > due;
  },

  /**
   * Get days until due or days overdue
   */
  getDaysFromDue: (dueDate) => {
    if (!dueDate) return null;

    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = Math.abs(due - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return today > due ? -diffDays : diffDays;
  },

  /**
   * Group payments by status
   */
  groupPaymentsByStatus: (payments) => {
    return {
      valid: payments.filter((p) => p.status === "Valid"),
      pending: payments.filter((p) => p.status === "Pending Validation"),
      suspense: payments.filter((p) => p.status === "Suspense"),
      rejected: payments.filter((p) => p.status === "Rejected"),
    };
  },

  /**
   * Group fees by status
   */
  groupFeesByStatus: (fees) => {
    return {
      pending: fees.filter((f) => f.status === "Pending"),
      partiallyPaid: fees.filter((f) => f.status === "Partially Paid"),
      fullyPaid: fees.filter((f) => f.status === "Fully Paid"),
      overdue: fees.filter(
        (f) =>
          FinanceService.isOverdue(f.due_date) && f.status !== "Fully Paid",
      ),
    };
  },

  /**
   * Calculate total amount due for student
   */
  calculateTotalDue: (fees) => {
    return fees.reduce((sum, fee) => sum + parseFloat(fee.amount_due || 0), 0);
  },

  /**
   * Calculate total amount paid for student
   */
  calculateTotalPaid: (fees) => {
    return fees.reduce((sum, fee) => sum + parseFloat(fee.amount_paid || 0), 0);
  },

  /**
   * Calculate outstanding balance
   */
  calculateOutstandingBalance: (fees) => {
    const totalDue = FinanceService.calculateTotalDue(fees);
    const totalPaid = FinanceService.calculateTotalPaid(fees);
    return totalDue - totalPaid;
  },

  /**
   * Parse error response
   */
  parseError: (response) => {
    if (typeof response === "string") return response;
    if (response.msg) return response.msg;
    if (response.message) return response.message;
    if (response.error) return response.error;
    return "An error occurred";
  },

  /**
   * Validate payment amount
   */
  validatePaymentAmount: (amount, maxAmount = null) => {
    if (!amount || isNaN(amount)) return "Invalid amount";
    if (amount <= 0) return "Amount must be greater than 0";
    if (maxAmount && amount > maxAmount)
      return `Amount cannot exceed ${FinanceService.formatCurrency(maxAmount)}`;
    return null;
  },

  /**
   * Generate payment reference
   */
  generatePaymentReference: (prefix = "PAY") => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `${prefix}-${timestamp}-${random}`;
  },

  /**
   * Format date for display
   */
  formatDate: (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  },

  /**
   * Get payment method badge color
   */
  getPaymentMethodColor: (method) => {
    const colors = {
      Mpesa: "green",
      "Mobile Money": "green",
      "Bank Transfer": "blue",
      Card: "orange",
      SurePay: "cyan",
      SchoolPay: "purple",
      Cash: "default",
    };
    return colors[method] || "default";
  },
};

// Financial Overview and its discount preview display amounts to two decimals.
export const formatCurrencyWithDecimals = (value) => {
  const amount = Number(value);
  return FinanceService.formatCurrency(Number.isFinite(amount) ? amount : 0, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default FinanceService;
